'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';

const VideoSection = ({ context }) => {
    const swiperRef = useRef(null);
    const [loadedVideos, setLoadedVideos] = useState({}); // To store loaded video state

    const handleSlideChange = () => {
        // When slide changes, pause all videos.
        pauseAllVideos();
    };

    const pauseAllVideos = () => {
        // Pause videos by sending a message to video elements
        const videos = document.querySelectorAll('video');
        videos.forEach((video) => {
            video.pause();
        });
    };

    const loadVideo = (index, videoUrl) => {
        // Lazy-load video URL when the video is in view
        setLoadedVideos((prevState) => ({
            ...prevState,
            [index]: videoUrl, // Set the loaded video URL
        }));
    };

    const handleBeforeUnload = () => {
        // Pause all videos before page unload
        pauseAllVideos();
    };

    // Setup event listener for page unload
    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    // Using IntersectionObserver to load videos only when they come into view
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !loadedVideos[entry.target.dataset.index]) {
                        loadVideo(entry.target.dataset.index, entry.target.dataset.video);
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe all video containers
        const videoContainers = document.querySelectorAll('.video-container');
        videoContainers.forEach((container) => observer.observe(container));

        return () => {
            observer.disconnect();
        };
    }, [loadedVideos]);

    return (
        <section className="relative max-w-7xl px-6 sm:px-3 z-10 mx-auto">
            <div className="max-w-3xl mx-auto md:mb-4 font-bold leading-10 text-black text-center text-[30px] mb-6">
                {context?.header}
            </div>

            <div className="relative mx-10 sm:mx-3">
                <Swiper
                    ref={swiperRef}
                    modules={[Navigation]}
                    onSlideChange={handleSlideChange}
                    navigation={{
                        nextEl: '.next',
                        prevEl: '.prev',
                    }}
                    loop
                    slidesPerView={1}
                    speed={1000}
                >
                    {(context?.videos || []).map((video, index) => (
                        <SwiperSlide key={video} className="px-5 sm:px-2">
                            <div
                                className="video-container xl:w-[768px] w-[566px] sm:w-[80%] xl:h-[432px] h-[400px] sm:h-[350px] xs:h-[250px] mx-auto max-w-3xl"
                                data-index={index}
                                data-video={video}
                            >
                                {/* Lazy-load video tag only when video is in view */}
                                {loadedVideos[index] ? (
                                    <video
                                        title={`video-${index}`}
                                        src={loadedVideos[index]}
                                        width="100%"
                                        height="100%"
                                        controls
                                        muted
                                        playsInline
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gray-300 flex items-center justify-center">Loading...</div>
                                )}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            <div className="mt-4 ml-4 block">
                <button
                    aria-label="prev"
                    className="prev z-20 absolute left-[5%] sm:left-2 top-[50%]"
                >
                    <div
                        role="button"
                        className="outline-none cursor-pointer prev rounded-full border-2 border-[#eb532e] p-1 sm:p-0 group transition-all duration-200"
                        aria-label="Previous slide"
                    >
                        <svg
                            data-name="Layer 2"
                            width="30"
                            height="30"
                            fill="#eb532e"
                            id="ba2a3e4c-ba93-42b4-a09d-e00e384488f4"
                            viewBox="0 0 38 38"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <polygon points="5.525 19 13.039 26.514 13.039 11.486 5.525 19"></polygon>
                            <rect
                                height="2.5"
                                width="21.79"
                                x="10.686"
                                y="17.75"
                            ></rect>
                        </svg>
                    </div>
                </button>
                <button
                    aria-label="next"
                    className="next z-20 absolute right-[5%] sm:right-2 top-[50%]"
                >
                    <div
                        role="button"
                        className="outline-none cursor-pointer next rounded-full border-2 border-[#eb532e] p-1 sm:p-0 group transition-all duration-200"
                        aria-label="Next slide"
                    >
                        <svg
                            data-name="Layer 2"
                            width="30"
                            height="30"
                            fill="#eb532e"
                            id="fe313a8e-a2a0-43e0-8273-3419f5c40aca"
                            viewBox="0 0 38 38"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <polygon points="32.475 19 24.961 26.514 24.961 11.486 32.475 19"></polygon>
                            <rect
                                height="2.5"
                                transform="translate(32.839 38) rotate(-180)"
                                width="21.79"
                                x="5.525"
                                y="17.75"
                            ></rect>
                        </svg>
                    </div>
                </button>
            </div>
        </section>
    );
};

export default VideoSection;
